// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import * as React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import {
    Icon,
    TextField,
    ComboBox,
    IComboBoxStyles,
    IDropdownOption,
    IComboBoxOption,
    Checkbox,
    PrimaryButton,
    Spinner,
} from 'office-ui-fabric-react';

import { LoadingState } from './../../store/actionStatus/actionStatusTypes';

import ScreenShotGenerator from './ScreenShotGenerator';

import Styles from './feedback.module.scss';

const feedbackCategoryStyle: Partial<IComboBoxStyles> = {
    container: {
        width: '100%',
    },
    optionsContainerWrapper: {
        maxHeight: 350,
        width: 330,
    },
};

export interface PropsType {
    data: {
        feedbackType: string;
        feedbackTitle: string;
        feedbackDetails: string;
        includeScreenshots: boolean;
    };
    isOpen: boolean;
    onCategoryChange: any;
    onCloseClicked: any;
    onDetailsChange: any;
    onFeedbackSubmitClick: any;
    onIncludeScreenshotsChange: any;
    onTitleChange: any;
    submitCallStatus: any;
    onScreenshotCaptured: any;
}

export default function DialogForm(props: PropsType) {
    const { t } = useTranslation();

    const [isMinimized, setIsMinimized] = React.useState<boolean>(false);
    const [showMessage, setShowMessage] = React.useState<boolean>(false);

    const feedbackOptions: IComboBoxOption[] = [
        {
            text: t('bug'),
            key: 'bug',
        },
        {
            text: t('feature_request'),
            key: 'feature_request',
        },
        {
            text: t('compliment'),
            key: 'compliment',
        },
    ];

    const { data } = props;

    function onCategoryChange(event: any, selectedOption?: IDropdownOption) {
        if (selectedOption) {
            const { key, text } = selectedOption;

            props.onCategoryChange(key.toString(), text);
        }
    }

    function onFeedbackSubmitClick() {
        props.onFeedbackSubmitClick();
        setShowMessage(true);
    }

    function onCloseClicked() {
        props.onCloseClicked();
        setShowMessage(false);
    }

    function onMinimizeClicked() {
        setIsMinimized(!isMinimized);
    }

    function isSubmittable() {
        return !!(
            data.feedbackDetails.length > 0 &&
            data.feedbackTitle.length > 0 &&
            data.feedbackType &&
            props.submitCallStatus.status !== LoadingState.started
        );
    }

    if (!props.isOpen) {
        return null;
    }

    const containerStyle = clsx(Styles.container, 't-main-container', {
        [Styles.minimize]: isMinimized,
    });

    const formContainerStyle = clsx(Styles.formContainer, {
        [Styles.hidden]: isMinimized,
    });

    const referenceNumber =
        showMessage && (props.submitCallStatus.status === LoadingState.success)
            ? props.submitCallStatus.data && props.submitCallStatus.data.Status
            : '';

    return (
        <div className={containerStyle}>
            <section className={Styles.headerContainer}>
                <header className={Styles.title}>{t('feedback')}</header>
                <div className={Styles.iconsContainer}>
                    <button
                        className={Styles.minimizeContainer}
                        onClick={onMinimizeClicked}
                    >
                        <Icon
                            ariaLabel={t('minimize_feedback_form')}
                            className={Styles.closeIcon}
                            iconName={
                                isMinimized ? 'FieldEmpty' : 'ChromeMinimize'
                            }
                        />
                    </button>
                    <button
                        className={Styles.closeContainer}
                        onClick={onCloseClicked}
                    >
                        <Icon
                            ariaLabel={t('close_feedback_form')}
                            className={Styles.closeIcon}
                            iconName="ChromeClose"
                        />
                    </button>
                </div>
            </section>
            {referenceNumber ? (
                <div className={Styles.referenceNumberContainer}>
                    <label className={Styles.referenceNumber}>
                        {t('feedback_saved', { referenceNumber })}
                    </label>
                </div>
            ) : (
                <section className={formContainerStyle}>
                    <ComboBox
                        ariaLabel={t('categorize_your_feedback')}
                        autoComplete="on"
                        label={t('categorize_your_feedback')}
                        onChange={onCategoryChange}
                        options={feedbackOptions}
                        selectedKey={data.feedbackType}
                        styles={feedbackCategoryStyle}
                    />
                    <TextField
                        ariaLabel={t('title')}
                        className={Styles.inputs}
                        label={t('title')}
                        onChange={props.onTitleChange}
                        value={data.feedbackTitle}
                    />
                    <TextField
                        ariaLabel={t('details')}
                        className={Styles.inputs}
                        label={t('details')}
                        multiline={true}
                        onChange={props.onDetailsChange}
                        value={data.feedbackDetails}
                    />
                    <Checkbox
                        className={Styles.inputs}
                        defaultChecked={data.includeScreenshots}
                        label={t('include_screenshots')}
                        onChange={props.onIncludeScreenshotsChange}
                    />
                    {data.includeScreenshots && (
                        <ScreenShotGenerator
                            onScreenshotCaptured={props.onScreenshotCaptured}
                        />
                    )}
                    <div className={Styles.submitContainer}>
                        <PrimaryButton
                            className={Styles.submitButton}
                            disabled={!isSubmittable()}
                            onClick={onFeedbackSubmitClick}
                            text={t('send_feedback')}
                        />
                        {props.submitCallStatus.status ===
                            LoadingState.started && <Spinner />}
                    </div>
                </section>
            )}
        </div>
    );
}